import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { horseAPI, breedsAPI, eventAPI } from '@/services/api';
import { Search, ArrowRight, MapPin, Star, Eye, MessageSquare, CreditCard, CalendarDays, Trophy, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getTranslatedName, createTranslationMap } from '@/utils/i18nHelpers';

// const HERO_IMG = "https://images.unsplash.com/photo-1675879102007-bc8331c58c46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxlcXVlc3RyaWFuJTIwaG9yc2UlMjBwb3J0cmFpdCUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzM1MDA3MDd8MA&ixlib=rb-4.1.0&q=85";
const HERO_IMG = "/main_image.png";

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [featuredHorses, setFeaturedHorses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [breeds, setBreeds] = useState([]);
	const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
		horseAPI
			.list({ limit: 6, sort: 'newest' })
			.then(r => setFeaturedHorses(r.data.items || []))
			.catch(() => {});
		eventAPI
			.list({ limit: 3, sort: 'upcoming' })
			.then(r => setUpcomingEvents(r.data.items || []))
			.catch(() => {});
    breedsAPI
			.list()
			.then(r => setBreeds(r.data || []))
			.catch(() => {});
  }, []);

  const breedMap = createTranslationMap(breeds, i18n.language);

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birth = new Date(dob);
    const now = new Date();
    return Math.floor((now - birth) / (365.25 * 24 * 60 * 60 * 1000));
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[85vh] min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Equestrian" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0"
							 style={{ background: 'linear-gradient(to bottom, rgba(26,60,52,0.4), rgba(26,60,52,0.85))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <p className="text-[#D4C5A9] text-sm font-semibold tracking-[0.2em] uppercase mb-4 animate-fade-in-up font-['Barlow_Condensed']">
							{t('home.hero_above_title')}
            </p>
            <h1 className="text-4xl md:text-6xl text-white font-medium tracking-tight leading-[1.1] mb-6 animate-fade-in-up stagger-1 font-['Playfair_Display']">
              {t('home.hero_title')}
            </h1>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-8 animate-fade-in-up stagger-2 font-['Manrope']">
              {t('home.hero_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 animate-fade-in-up stagger-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666055]" />
                <Input
                  data-testid="hero-search"
                  placeholder={t('home.search_placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 bg-white/95 backdrop-blur border-0 rounded-sm text-[#1A3C34] placeholder:text-[#666055]/60 text-base"
                  onKeyDown={(e) => { if (e.key === 'Enter' && searchQuery) window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`; }}
                />
              </div>
              <Button
                data-testid="hero-search-btn"
                onClick={() => { if (searchQuery) window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`; }}
                className="h-14 bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm px-8 text-base font-medium btn-equi"
              >
                {t('common.search')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Breed Categories */}
      {breeds.length > 0 && (
        <section className="py-20 md:py-28 bg-white" data-testid="breeds-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-[#8B4513] text-sm font-semibold tracking-[0.15em] uppercase mb-2 font-['Barlow_Condensed']">
									{t('home.categories')}
								</p>
                <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-[#1A3C34] font-['Playfair_Display']">
									{t('home.popular_breeds')}
								</h2>
              </div>
              <Link to="/horses" className="hidden md:flex items-center gap-2 text-sm font-medium text-[#8B4513] hover:text-[#6F3610] transition-colors"
										data-testid="view-all-breeds">
                {t('home.view_all')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {breeds.filter(b => b.popular === true).map((breed, i) => (
                <Link
                  key={breed.id}
                  to={`/horses?breed=${breed.name}`}
                  className={`group p-6 border border-[#E6E2D6] bg-[#F9F7F2] hover:bg-[#1A3C34] rounded-sm transition-colors duration-300 animate-fade-in-up stagger-${i + 1}`}
                  data-testid={`breed-card-${breed.name}`}
                >
                  <h3 className="text-lg font-semibold text-[#1A3C34] group-hover:text-white transition-colors font-['Playfair_Display']">
										{getTranslatedName(breed, i18n.language)}
									</h3>
                  <p className="text-xs text-[#666055] group-hover:text-white/60 mt-1 transition-colors">
										{breed.origin}
									</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Horses */}
      <section className="py-20 md:py-28" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-[#8B4513] text-sm font-semibold tracking-[0.15em] uppercase mb-2 font-['Barlow_Condensed']">
								{t('home.featured_horses')}
							</p>
              <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-[#1A3C34] font-['Playfair_Display']">
								{t('home.latest_listings')}
							</h2>
            </div>
            <Link to="/horses" className="hidden md:flex items-center gap-2 text-sm font-medium text-[#8B4513] hover:text-[#6F3610] transition-colors">
              {t('home.view_all')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {featuredHorses.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredHorses.map((horse, i) => (
                <Link
                  key={horse.id}
                  to={`/horses/${horse.slug || horse.id}`}
                  className={`horse-card group bg-white border border-[#E6E2D6] rounded-sm overflow-hidden animate-fade-in-up stagger-${(i % 4) + 1}`}
                  data-testid={`horse-card-${horse.id}`}
                >
                  <div className="aspect-[4/3] bg-[#F2F0E9] overflow-hidden relative">
                    {horse.images?.[0] ? (
                      <img src={horse.images[0].startsWith('http') ? horse.images[0] : `${process.env.REACT_APP_BACKEND_URL}${horse.images[0]}`}
													 alt={horse.title}
													 className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#D4C5A9]">
                        <Star className="w-12 h-12" />
                      </div>
                    )}
                    {horse.is_featured && (
                      <span className="absolute top-3 left-3 featured-badge px-3 py-1 text-[10px] font-bold tracking-wider uppercase text-[#1A3C34] rounded-sm">
                        {t('horse.featured')}
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-[#1A3C34] group-hover:text-[#8B4513] transition-colors line-clamp-1 font-['Playfair_Display']">
												{horse.title}
											</h3>
                    </div>
                    <p className="text-2xl font-bold text-[#8B4513] mb-3 font-['Manrope']">
											&euro;{horse.price?.toLocaleString()}
										</p>
                    <div className="flex items-center gap-4 text-xs text-[#666055]">
                      { horse.breed &&
												<span>{breedMap[horse.breed] || horse.breed}</span>
											}
                      { horse.date_of_birth &&
												<span>{calculateAge(horse.date_of_birth)} {t('horse.years')}</span>
											}
                      { horse.height &&
												<span>{horse.height} {t('horse.cm')}</span>
											}
                    </div>
                    {horse.location_name && (
                      <div className="flex items-center gap-1 mt-3 text-xs text-[#666055]/70">
                        <MapPin className="w-3 h-3" /> {horse.location_name}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-[#666055]">
              <Star className="w-12 h-12 mx-auto mb-4 text-[#D4C5A9]" />
              <p className="text-lg">{ t('home.no_horses_message') }</p>
              <Link to="/sell/horse">
                <Button className="mt-4 bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm px-8 h-12">
                  {t('horse.create_ad')}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

			{/* Upcoming Events */}
			{upcomingEvents.length > 0 && (
				<section className="py-20 md:py-28 bg-white" data-testid="upcoming-events-section">
					<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
						<div className="flex items-end justify-between mb-10 flex-wrap gap-4">
							<div>
								<p className="text-[#8B4513] text-sm font-semibold tracking-[0.15em] uppercase mb-2
										font-['Barlow_Condensed']">
									{t('home.upcoming_events_kicker')}
								</p>
								<h2 className="text-3xl md:text-5xl font-medium tracking-tight text-[#1A3C34]
										font-['Playfair_Display']">{t('home.upcoming_events_title')}</h2>
							</div>
							<Link to="/events" className="inline-flex items-center gap-2 text-sm font-semibold text-[#8B4513]
									hover:text-[#1A3C34] transition-colors group" data-testid="home-view-all-events">
								{t('home.view_all_events')}
								<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
							</Link>
						</div>
						<div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="upcoming-events-grid">
							{upcomingEvents.map((ev, i) => {
								const start = ev.start_date ? new Date(ev.start_date + 'T00:00:00') : null;
								const end = ev.end_date ? new Date(ev.end_date + 'T00:00:00') : null;
								const isMultiDay = end && ev.end_date !== ev.start_date;
								return (
									<Link
										key={ev.id}
										to={`/events/${ev.slug || ev.id}`}
										className={`horse-card group bg-[#F9F7F2] border border-[#E6E2D6] rounded-sm overflow-hidden 
											flex flex-col animate-fade-in-up stagger-${i + 1}`}
										data-testid={`home-event-${ev.id}`}
									>
										<div className="relative aspect-[4/3] bg-gradient-to-br from-[#1A3C34] to-[#8B4513] flex
												items-center justify-center overflow-hidden">
											{ev.images?.[0] ? (
												<img src={ev.images[0].startsWith('http') ? ev.images[0] :
													`${process.env.REACT_APP_BACKEND_URL}${ev.images[0]}`} alt={ev.title}
														 className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
														 loading="lazy" />
											) : (
												<CalendarDays className="w-16 h-16 text-white/80" />
											)}
											<span className="absolute top-3 left-3 text-[10px] bg-white/95 text-[#1A3C34] px-2 py-1
													rounded-sm font-semibold uppercase tracking-wider">
												{t(`event.type.${ev.event_type || 'other'}`)}
											</span>
											{start && (
												<div className="absolute top-3 right-3 bg-white/95 rounded-sm px-2.5 py-1.5 text-center min-w-[52px]">
													<div className="text-[9px] font-bold text-[#8B4513] uppercase tracking-wider leading-none">
														{start.toLocaleDateString('en', { month: 'short' })}
													</div>
													<div className="text-xl font-bold text-[#1A3C34] leading-none mt-0.5 font-['Playfair_Display']">
														{start.getDate()}
													</div>
												</div>
											)}
										</div>
										<div className="p-5 flex-1 flex flex-col">
											<h3 className="text-lg font-semibold text-[#1A3C34] group-hover:text-[#8B4513]
													transition-colors line-clamp-2 font-['Playfair_Display']">{ev.title}</h3>
											{isMultiDay && (
												<p className="text-[10px] text-[#666055] mt-1 uppercase tracking-wider">
													{start.toLocaleDateString('en', { day: '2-digit', month: 'short' })} –
													{end.toLocaleDateString('en', { day: '2-digit', month: 'short', year: 'numeric' })}
												</p>
											)}
											<div className="flex items-baseline gap-1 mt-2">
												{ev.is_free ? (
													<p className="text-xl font-bold text-[#1A3C34]">{t('event.free')}</p>
												) : (
													<>
														<p className="text-xl font-bold text-[#8B4513]">&euro;{ev.price?.toLocaleString()}</p>
														<span className="text-xs text-[#666055]">{t('event.entry')}</span>
													</>
												)}
											</div>
											<div className="flex items-center gap-3 mt-2 text-xs text-[#666055]/80 flex-wrap">
												{typeof ev.prize_amount === 'number' && ev.prize_amount > 0 && (
													<span className="inline-flex items-center gap-1">
														<Trophy className="w-3 h-3" />&euro;{ev.prize_amount?.toLocaleString()}
													</span>
												)}
												{typeof ev.max_participants === 'number' && ev.max_participants > 0 && (
													<span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />
														{ev.max_participants}
													</span>
												)}
											</div>
											{ev.location_name && (
												<div className="flex items-center gap-1 mt-auto pt-3 text-xs text-[#666055]/70">
													<MapPin className="w-3 h-3" />{ev.location_name}
												</div>
											)}
										</div>
									</Link>
								);
							})}
						</div>
					</div>
				</section>
			)}

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#8B4513] text-sm font-semibold tracking-[0.15em] uppercase mb-2 font-['Barlow_Condensed']">
							{t('home.how_it_works')}
						</p>
            <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-[#1A3C34] font-['Playfair_Display']">
							{t('home.three_steps')}
						</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { icon: Eye, title: t('home.step1_title'), desc: t('home.step1_desc'), num: '01' },
              { icon: MessageSquare, title: t('home.step2_title'), desc: t('home.step2_desc'), num: '02' },
              { icon: CreditCard, title: t('home.step3_title'), desc: t('home.step3_desc'), num: '03' },
            ].map((step, i) => (
              <div key={i} className={`text-center animate-fade-in-up stagger-${i + 1}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1A3C34]/5 mb-6">
                  <step.icon className="w-7 h-7 text-[#1A3C34]" />
                </div>
                <span className="block text-5xl font-bold text-[#E6E2D6] mb-2 font-['Playfair_Display']">
									{step.num}
								</span>
                <h3 className="text-xl font-semibold text-[#1A3C34] mb-2 font-['Playfair_Display']">
									{step.title}
								</h3>
                <p className="text-sm text-[#666055] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#1A3C34] relative overflow-hidden" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-white mb-6 font-['Playfair_Display']">
            {t('home.cta_title')}
          </h2>
          <p className="text-lg text-white/60 mb-10 max-w-xl mx-auto">
            {t('home.cta_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/horses">
              <Button className="bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm px-10 h-14 text-base
              		font-medium btn-equi">
                {t('nav.horses')}
              </Button>
            </Link>
            <Link to="/auth">
              <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10
              		rounded-sm px-10 h-14 text-base font-medium">
                {t('nav.register')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
